<template>
  <v-dialog v-model="dialog" max-width="720" persistent>
    <v-card class="vehicle-conn-wizard">
      <v-card-title class="d-flex align-center">
        {{ isChange ? $t('vehicle.conn-wizard.title-change') : $t('vehicle.conn-wizard.title') }}
        <v-spacer />
        <div class="cw-steps">
          <div
              v-for="(s, i) in visibleSteps"
              :key="s.key"
              class="cw-step"
              :class="{ 'cw-step--active': stepIndex === i, 'cw-step--done': stepIndex > i }"
          >
            <span class="cw-step__num"><v-icon v-if="stepIndex > i" x-small color="white">mdi-check</v-icon><template v-else>{{ i + 1 }}</template></span>
            <span class="cw-step__label">{{ s.label }}</span>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="pt-4">

        <!-- No offer at all -->
        <v-alert v-if="loaded && !hasAnyOffer" type="warning" text dense>
          {{ $t('vehicle.conn-wizard.no-offer') }}
        </v-alert>

        <template v-else-if="loaded">
          <!-- Existing connection → this is a change, not a first init -->
          <v-alert v-if="isChange" type="info" text dense class="mb-4">
            {{ $t('vehicle.conn-wizard.change-hint', { mode: currentModeLabel, type: currentTypeLabel }) }}
          </v-alert>

          <!-- STEP mode: telemetry vs meter -->
          <section v-show="currentKey === 'mode'" class="cw-body">
            <p class="mb-4">{{ $t('vehicle.conn-wizard.mode-intro') }}</p>
            <v-radio-group v-model="connectionMode" class="mt-0">
              <v-radio value="telemetry" :disabled="!modeAvailable('telemetry')">
                <template #label>
                  <div>
                    <div>{{ $t('vehicle.conn-wizard.mode-telemetry') }}</div>
                    <div class="text-caption text--secondary">{{ $t('vehicle.conn-wizard.mode-telemetry-hint') }}</div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="meter" :disabled="!modeAvailable('meter')">
                <template #label>
                  <div>
                    <div>{{ $t('vehicle.conn-wizard.mode-meter') }}</div>
                    <div class="text-caption text--secondary">{{ $t('vehicle.conn-wizard.mode-meter-hint') }}</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </section>

          <!-- STEP member (only when the vehicle has no driver yet): pick an EXISTING member (they carry the
               customer the vehicle is linked to and whose record the consent later updates). -->
          <section v-show="currentKey === 'member'" class="cw-body">
            <p class="mb-2">{{ $t('vehicle.conn-wizard.customer-intro') }}</p>
            <member-select
                v-model="ownerMemberId"
                :organization-id="vehicleOrganizationId"
                :label="$t('vehicle.wizard.choose-driver')"
                clearable
            />
          </section>

          <!-- STEP type -->
          <section v-show="currentKey === 'type'" class="cw-body">
            <div class="text-subtitle-2 mb-2">{{ $t('vehicle.wizard.connection-type') }}</div>
            <v-radio-group v-model="connectionType" class="mt-0">
              <v-radio v-for="opt in typeOptions" :key="opt.value" :value="opt.value" :disabled="!opt.available">
                <template #label>
                  <div>
                    <div>{{ opt.label }}</div>
                    <div class="text-caption text--secondary">{{ opt.hint }}</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>

            <!-- VIN (VIN-type connections). Plain 17-char format check — no web service call here. -->
            <div v-if="connectionType === 'vin'" class="mt-2">
              <v-text-field
                  :label="$t('vehicle.vin')" v-model="vin" outlined dense
                  @input="vin = (vin || '').toUpperCase()"
                  counter="17" maxlength="17" hide-details="auto"
                  :rules="[vinRule]"
              />
            </div>

            <v-alert v-if="resolvedFormula" type="success" text dense class="mt-2">
              {{ $t('vehicle.wizard.formula-resolved', { name: resolvedFormula.name }) }}
              <strong v-if="resolvedFormula.price">· {{ resolvedFormula.price }} €</strong>
            </v-alert>
          </section>

          <!-- STEP consent -->
          <section v-show="currentKey === 'consent'" class="cw-body text-center py-4">
            <v-icon size="64" color="primary" class="mb-4">mdi-shield-check-outline</v-icon>
            <div class="text-h6 mb-2">{{ $t('vehicle.wizard.consent-title') }}</div>
            <p class="text--secondary mb-2">{{ $t('vehicle.wizard.consent-intro') }}</p>
            <v-alert type="info" text dense class="text-left mt-4">
              {{ connectionMode === 'meter' ? $t('vehicle.wizard.consent-scope-meter') : $t('vehicle.conn-wizard.consent-scope-telemetry') }}
            </v-alert>
          </section>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn v-if="stepIndex > 0" small text @click="stepIndex--">
          <v-icon left small>mdi-arrow-left</v-icon>{{ $t('back') }}
        </v-btn>
        <v-spacer />
        <v-btn small @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn v-if="currentKey !== 'consent'" small color="primary" :disabled="!canAdvance" @click="stepIndex++">
          {{ $t('next') }}
        </v-btn>
        <v-btn v-else small color="primary" :loading="saving" @click="finish">
          {{ $t('vehicle.wizard.finish') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository"
import MemberSelect from "@blocks/Select/MemberSelect.vue"

export default {
  name: 'VehicleConnectionWizardDialog',
  components: { MemberSelect },

  props: {
    value: Boolean,
    vehicle: Object, // the existing vehicle to initialise
    hasMeterLocation: Boolean, // the meter already has a home location (no need to pick one)
    currentMode: String, // set when the vehicle is ALREADY connected → this is a change
    currentType: String,
  },

  data() {
    return {
      loaded: false,
      stepIndex: 0,
      formulas: [],
      connectionMode: null,
      ownerMemberId: null, // the selected existing driver (member)
      connectionType: null,
      vin: '',
      saving: false,
    }
  },

  computed: {
    dialog: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },

    vehicleOrganizationId() {
      return this.vehicle?.organization?.id || null
    },

    isChange() {
      return !!this.currentMode
    },

    currentModeLabel() {
      if (this.currentMode === 'meter') return this.$t('vehicle.conn-wizard.mode-meter')
      if (this.currentMode === 'telemetry') return this.$t('vehicle.conn-wizard.mode-telemetry')
      return ''
    },

    currentTypeLabel() {
      if (this.currentType === 'vin') return this.$t('vehicle.wizard.type-vin')
      if (this.currentType === 'oauth') return this.$t('vehicle.wizard.type-oauth')
      return ''
    },

    hasAnyOffer() {
      return this.formulas.length > 0
    },

    // A driver step only when the vehicle has none yet. No location step — the home address is collected on
    // the consent page.
    visibleSteps() {
      const steps = [{ key: 'mode', label: this.$t('vehicle.conn-wizard.step-mode') }]
      if (!this.vehicle?.customer) {
        steps.push({ key: 'member', label: this.$t('vehicle.wizard.step-owner') })
      }
      steps.push({ key: 'type', label: this.$t('vehicle.wizard.step-connection') })
      steps.push({ key: 'consent', label: this.$t('vehicle.wizard.step-consent') })
      return steps
    },

    currentKey() {
      return this.visibleSteps[this.stepIndex]?.key
    },

    typeOptions() {
      const has = t => this.formulas.some(f => f.mode === this.connectionMode && f.type === t)
      return [
        { value: 'vin', label: this.$t('vehicle.wizard.type-vin'), hint: this.$t('vehicle.wizard.type-vin-hint'), available: has('vin') },
        { value: 'oauth', label: this.$t('vehicle.wizard.type-oauth'), hint: this.$t('vehicle.wizard.type-oauth-hint'), available: has('oauth') },
      ]
    },

    resolvedFormula() {
      if (!this.connectionMode || !this.connectionType) return null
      return this.formulas.find(f => f.mode === this.connectionMode && f.type === this.connectionType) || null
    },

    // A VIN is 17 chars, uppercase alphanumeric, excluding I/O/Q.
    vinValid() {
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(this.vin || '')
    },

    canAdvance() {
      switch (this.currentKey) {
        case 'mode': return !!this.connectionMode
        case 'member': return !!this.ownerMemberId
        case 'type': return !!this.resolvedFormula && (this.connectionType !== 'vin' || this.vinValid)
        default: return true
      }
    },
  },

  watch: {
    value(val) {
      if (val) this.reset()
    },
    connectionMode() {
      // Keep the current type if it's still available in the new mode, else default to the first available.
      if (!this.typeOptions.find(o => o.value === this.connectionType && o.available)) {
        this.connectionType = this.typeOptions.find(o => o.available)?.value || null
      }
    },
  },

  methods: {
    reset() {
      this.stepIndex = 0
      this.connectionMode = null
      this.ownerMemberId = null
      this.connectionType = null
      // Pre-fill the VIN when the vehicle already has one (it is on the registration document) — the user
      // can confirm or correct it.
      this.vin = this.vehicle?.vin || ''
      this.saving = false
      this.loaded = false
      VehicleRepository.getConnectableFormulas(this.vehicle.id).then(formulas => {
        this.formulas = formulas || []
        if (this.isChange && this.modeAvailable(this.currentMode)) {
          // Pre-fill the vehicle's current connection so a change starts from what exists.
          this.connectionMode = this.currentMode
          this.connectionType = this.currentType
        } else if (this.modeAvailable('meter') && !this.modeAvailable('telemetry')) {
          this.connectionMode = 'meter'
        } else if (this.modeAvailable('telemetry') && !this.modeAvailable('meter')) {
          this.connectionMode = 'telemetry'
        }
        this.loaded = true
      }).catch(() => { this.formulas = []; this.loaded = true })
    },

    close() {
      this.dialog = false
    },

    modeAvailable(mode) {
      return this.formulas.some(f => f.mode === mode)
    },

    vinRule(v) {
      if (!v) return this.$t('required')
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(v) || this.$t('vehicle.wizard.vin-invalid')
    },

    // Attach the selected driver (member) when the vehicle has none, then send the consent link. Nothing else
    // is created now: the licence, the meter and the provider registration are materialised at consent. The
    // config (mode, type, VIN) travels on the consent; the home address is collected on the consent page.
    finish() {
      if (!this.resolvedFormula) return
      this.saving = true

      let chain = Promise.resolve()
      // Only associate a driver when the vehicle has none yet.
      if (!this.vehicle.customer && this.ownerMemberId) {
        chain = chain.then(() => VehicleRepository.transfer(
            this.vehicle.id, { fromType: 'organization', toType: 'member', toId: this.ownerMemberId }))
      }

      const consent = { mode: this.connectionMode, type: this.connectionType }
      if (this.connectionType === 'vin' && this.vin) consent.vin = this.vin

      chain
          .then(() => VehicleRepository.requestTelematicsConsent(this.vehicle.id, consent))
          .then(() => {
            this.saving = false
            this.$emit('initialised')
            this.$dialog.notify.success(this.$t('vehicle.wizard.consent-sent'))
            this.close()
          })
          .catch(err => {
            this.saving = false
            this.notifyError(err)
          })
    },
  },
}
</script>

<style lang="scss">
.vehicle-conn-wizard {
  .cw-steps { display: flex; gap: 6px; flex-wrap: wrap; }
  .cw-step {
    display: flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px;
    background: #f4f6f8; color: #607d8b; font-size: 12px; font-weight: 600;
  }
  .cw-step__num { width: 18px; height: 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #cfd8dc; color: #fff; font-size: 11px; }
  .cw-step--active .cw-step__num { background: var(--v-primary-base, #e53c22); }
  .cw-step--done .cw-step__num { background: #4caf50; }
  .cw-body { animation: cwfade .2s ease; }
  @keyframes cwfade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
}
</style>
